import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  generateDocumentUploadUrl,
  getBillCheckDetails,
  startBillCheckProcess,
  submitBillCheckDetails,
  updateBillCheckDetails,
} from '@/api/billCheck/billCheckV3';
import type { Documents, BillcheckDetails, SubmitBillCheckReq } from '@/@types/billCheckV3';
import { CUSTOMER_DETAILS, ERROR } from '@/domain/routes';
import { useMFENavigation } from '@/customHooks/useMFENavigation.hook';

export const useStartBillCheckProcess = () => {
  const queryClient = useQueryClient();
  const { navigateToNestedModalPath } = useMFENavigation();
  const { mutate, isSuccess, data, isLoading } = useMutation(
    async () => {
      const data = await startBillCheckProcess();
      return data;
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(['products-status']);
        navigateToNestedModalPath(CUSTOMER_DETAILS);
      },
      onError: () => navigateToNestedModalPath(ERROR),
    },
  );
  return {
    startBillCheck: mutate,
    isStartBillCheckSuccess: isSuccess,
    startBillCheckRes: data,
    isBillCheckStarting: isLoading,
  };
};

export const useGetBillCheckDetails = (billCheckFlowId: string | undefined, forceEnable = false) => {
  const { isLoading, isFetching, data, isError, isSuccess } = useQuery({
    queryKey: ['billcheck-details', billCheckFlowId],
    queryFn: async () => getBillCheckDetails(billCheckFlowId),
    enabled: !!billCheckFlowId || forceEnable,
  });

  return {
    initializing: isLoading || isFetching,
    billCheckDetails: data,
    isBillCheckDetailsSuccess: isSuccess,
    isBillCheckDetailsError: isError,
  };
};

export const useUpdateBillCheckDetails = (billCheckFlowId: string) => {
  const { navigateToNestedModalPath } = useMFENavigation();
  const { mutate, isSuccess, isLoading } = useMutation(
    async (billcheckDetails: BillcheckDetails) => {
      const data = await updateBillCheckDetails(billCheckFlowId, billcheckDetails);
      return data;
    },
    {
      onError: () => navigateToNestedModalPath(ERROR),
    },
  );
  return {
    updateBillCheckDetails: mutate,
    isUpdateBillCheckDetailsSuccess: isSuccess,
    isUpdatingBillCheckDetails: isLoading,
  };
};

export const useSubmitBillCheckDetails = (billCheckFlowId: string) => {
  const { mutate, isSuccess } = useMutation(async (payload: SubmitBillCheckReq) => {
    const data = await submitBillCheckDetails(billCheckFlowId, payload);
    return data;
  });
  return { submitBillCheck: mutate, isBillCheckSubmitted: isSuccess };
};

export const useGenerateDocumentUploadUrl = (billCheckFlowId: string) => {
  const { mutate, data } = useMutation(async (documents: Documents) => {
    const data = await generateDocumentUploadUrl(billCheckFlowId, documents);
    return data;
  });
  return { generateDocumentUploadUrl: mutate, signedDocumentUploadUrls: data };
};
